import React from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomAdminModal from "../../common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { colors, semanticColors, spacing, radii, typographyScale } from "../../../styles/design-system";

interface PublishIssue {
  id: string;
  name: string;
  moduleTitle: string;
  type?: string;
}

interface PublishCourseWarningModalProps {
  visible: boolean;
  onClose: () => void;
  onContinue: () => void;
  assignmentsWithoutDueDates: PublishIssue[];
  unpublishedActivities: PublishIssue[];
  assessmentsWithoutAiTutors: PublishIssue[];
}

const PublishCourseWarningModal = ({
  visible,
  onClose,
  onContinue,
  assignmentsWithoutDueDates,
  unpublishedActivities,
  assessmentsWithoutAiTutors,
}: PublishCourseWarningModalProps) => {
  const { isMedium } = useResponsiveScreen();

  const renderGroupedList = (items: PublishIssue[], groupBy: "module" | "type" = "module") => {
    if (items.length === 0) return null;

    // Group items by module or type
    const grouped = items.reduce(
      (acc, item) => {
        const key = groupBy === "module" ? item.moduleTitle : item.type || "Other";
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      },
      {} as Record<string, PublishIssue[]>
    );

    return Object.entries(grouped).map(([groupKey, groupItems]) => (
      <View key={groupKey} style={styles.groupContainer}>
        <CustomText
          text={`Module: ${groupKey}`}
          size="m"
          weight="bold"
          style={styles.groupTitle}
          useTranslationText={false}
        />
        <View style={styles.listContainer}>
          {groupItems.map((item) => (
            <View key={item.id} style={styles.listItem}>
              <View style={styles.bullet} />
              <CustomText text={item.name} size="s" style={styles.listText} useTranslationText={false} />
            </View>
          ))}
        </View>
      </View>
    ));
  };

  const hasAnyIssues =
    assignmentsWithoutDueDates.length > 0 || unpublishedActivities.length > 0 || assessmentsWithoutAiTutors.length > 0;

  if (!hasAnyIssues) {
    return null;
  }

  return (
    <CustomAdminModal
      visible={visible}
      onRequestClose={onClose}
      title="Course Publish Warnings"
      ariaLabel="Course Publish Warnings Modal"
      style={{ width: isMedium ? "60%" : "90%", maxWidth: 800 }}
      onShow={() => {}}>
      <View style={styles.container}>
        <CustomText
          useTranslationText={false}
          text="The following issues were found in your course. These are informational only and will not prevent publishing."
          size="s"
          style={styles.warningText}
        />

        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={true}
          nestedScrollEnabled={true}>
          {assignmentsWithoutDueDates.length > 0 && (
            <View style={styles.section}>
              <CustomText
                text="Assignments Without Due Dates"
                size="m"
                weight="ultraBold"
                style={styles.sectionTitle}
                useTranslationText={false}
              />
              {renderGroupedList(assignmentsWithoutDueDates, "module")}
            </View>
          )}

          {unpublishedActivities.length > 0 && (
            <View style={styles.section}>
              <CustomText
                text="Unpublished Activities"
                size="m"
                weight="ultraBold"
                style={styles.sectionTitle}
                useTranslationText={false}
              />
              {renderGroupedList(unpublishedActivities, "module")}
            </View>
          )}

          {assessmentsWithoutAiTutors.length > 0 && (
            <View style={styles.section}>
              <CustomText
                text="Assessments Without AI Tutors"
                size="m"
                weight="ultraBold"
                style={styles.sectionTitle}
                useTranslationText={false}
              />
              {renderGroupedList(assessmentsWithoutAiTutors, "module")}
            </View>
          )}
        </ScrollView>

        <View style={styles.buttonContainer}>
          <CustomButton
            text="Cancel"
            onPress={onClose}
            styleType="secondary"
            size="m"
            style={styles.cancelButton}
            bold={false}
            leftIcon={null}
            rightIcon={null}
            disabled={false}
            textProps={{}}
            textStyle={styles.cancelButtonText}
          />
          <CustomButton
            text="Continue Publishing"
            onPress={onContinue}
            styleType="danger"
            size="m"
            style={styles.actionButton}
            bold={false}
            leftIcon={null}
            rightIcon={null}
            textStyle={styles.actionButtonText}
            disabled={false}
            textProps={{}}
          />
        </View>
      </View>
    </CustomAdminModal>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: spacing.lg,
    flex: 1,
    maxHeight: 600,
  },
  warningText: {
    color: semanticColors.typography.textLight,
    marginBottom: spacing.lg,
    lineHeight: typographyScale.textBase.lineHeight,
  },
  scrollView: {
    flex: 1,
    maxHeight: 400,
    marginBottom: spacing.lg,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  section: {
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    color: semanticColors.typography.displayPrimary,
    marginBottom: spacing.xs,
  },
  groupContainer: {
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
  },
  groupTitle: {
    color: semanticColors.typography.displayPrimary,
    marginBottom: spacing.xs,
  },
  listContainer: {
    marginTop: spacing.xxs,
  },
  listItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: spacing.xs,
  },
  bullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: semanticColors.typography.textLight,
    marginTop: spacing.xs,
    marginRight: spacing.xs + spacing.xxs,
  },
  listText: {
    color: semanticColors.typography.textLight,
    lineHeight: typographyScale.textBase.lineHeight,
    flex: 1,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: spacing.xs + spacing.xxs,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  cancelButton: {
    backgroundColor: semanticColors.surface.transparent,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.sm,
    paddingHorizontal: spacing.sm,
  },
  actionButton: {
    backgroundColor: semanticColors.surface.brand,
    borderRadius: radii.sm,
    paddingHorizontal: spacing.sm,
  },
  cancelButtonText: {
    color: semanticColors.typography.textLight,
  },
  actionButtonText: {
    color: semanticColors.typography.textWhite,
  },
});

export default PublishCourseWarningModal;
